/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Container } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import Link from 'components/Link';
import useTranslate from 'hooks/useTranslate';
import { useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';
import { selectCreditApplication } from 'state/creditApplication';

import Button from 'components/Button';
import PageContainer from 'components/PageContainer';
import { FormTextField } from 'components/TextField';

import Logo from 'features/Logo/Logo';
import { FormValues } from './OtpVerification';
import ConsumerSearchContext from '../ConsumerSearchContext';

import { subtitleText } from 'features/Theme';
import styles from './styles';
import CancelButton from 'features/CancelButton';
import IovationButton from 'features/IovationButton';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import SkipPrefillButton from 'features/SkipPrefillButton';

type PropTypes = FormikProps<FormValues> & {
  resendSms: () => Promise<unknown>;
  attemptsRemaining: string | null;
  tooManyAttempts: boolean;
  timedOut: boolean;
};

const OtpVerificationView: React.FC<PropTypes> = props => {
  const {
    isValid,
    isSubmitting,
    resendSms,
    attemptsRemaining,
    tooManyAttempts,
    timedOut,
    resetForm,
  } = props;

  const { i18nPrefix, skipToPath, hasExistingAccount } = useContext(
    ConsumerSearchContext,
  );
  const t = useTranslate();
  const tMd = useMarkdownTranslate();

  const handleResend = useCallback(async () => {
    await resendSms();
    resetForm();
  }, [resendSms, resetForm]);

  const creditApplication = useSelector(selectCreditApplication);
  const mobilePhone = creditApplication.mobilePhone as string;

  const lastFourPhone = mobilePhone
    ? mobilePhone.substring(mobilePhone.length - 4)
    : null;

  const contextualTitle = hasExistingAccount
    ? tMd(`${i18nPrefix}otpVerification.title`, {
        context: 'existingAccount',
        lastFourPhone: lastFourPhone,
      })
    : tMd(`${i18nPrefix}otpVerification.title`, {
        lastFourPhone: lastFourPhone,
      });

  return (
    <Fragment>
      <Container maxWidth="sm">
        <Logo />
      </Container>
      <PageContainer>
        <Form>
          <Container maxWidth="xs">
            <div css={styles.headingContainer}>
              <h1 css={subtitleText}>{contextualTitle}</h1>
            </div>

            {timedOut ? (
              <div>
                <p css={styles.errorText}>
                  {tMd(`${i18nPrefix}otpVerification.trackotpExpired`)}
                </p>
              </div>
            ) : null}

            <div css={styles.formFields}>
              <Field name="otpCode">
                {(fieldProps: FieldProps) => (
                  <FormTextField
                    id="otpCode"
                    fieldProps={fieldProps}
                    label={t(`formLabel.otpCode`)}
                    error={Boolean(attemptsRemaining)}
                    helperText={attemptsRemaining}
                    disabled={tooManyAttempts || timedOut}
                  />
                )}
              </Field>
            </div>
          </Container>

          <ActionButtonContainer>
            <IovationButton
              id="otpVerificationSubmit"
              type="submit"
              disabled={!isValid || isSubmitting || tooManyAttempts || timedOut}
              loading={isSubmitting}
            >
              {t(`${i18nPrefix}otpVerification.submit`)}
            </IovationButton>

            <Button id="resendOtp" onClick={handleResend} variant="text">
              {tMd(`${i18nPrefix}otpVerification.resendSubmit`)}
            </Button>

            {skipToPath ? (
              <SkipPrefillButton
                variant="text"
                id="continueWithoutSecureCode"
                component={Link}
                to={skipToPath}
              >
                {t(`${i18nPrefix}manualEntryButton`)}
              </SkipPrefillButton>
            ) : null}

            <CancelButton />
          </ActionButtonContainer>
        </Form>
      </PageContainer>
    </Fragment>
  );
};

export default OtpVerificationView;
